import type {
  EFilingStatus,
  IRSSubmission,
  IRSSubmissionDetail,
  NewSubmissionPayload,
  SubmissionEvent,
  SubmissionItem,
} from "@/features/efiling/types";
import { apiFetch } from "@/lib/api";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiItem {
  id: string;
  kind: SubmissionItem["kind"];
  position: number;
  document: string | null;
  form_submission: string | null;
  label: string;
  form_number: string;
}

interface ApiEvent {
  id: string;
  event_type: string;
  event_type_display: string;
  is_system: boolean;
  detail: string;
  created_at: string;
}

interface ApiSubmission {
  id: string;
  client: string;
  case: string | null;
  channel: IRSSubmission["channel"];
  channel_display: string;
  status: IRSSubmission["status"];
  status_display: string;
  practitioner: string;
  practitioner_name: string;
  practitioner_credentials_ready: { efin: boolean; caf: boolean };
  irs_confirmation_number: string;
  irs_ack_status: string;
  irs_ack_code: string;
  error_message: string;
  receipt_document: string | null;
  submitted_at: string | null;
  transmitted_at: string | null;
  acked_at: string | null;
  created_at: string;
  updated_at: string;
  items: ApiItem[];
}

interface ApiSubmissionDetail extends ApiSubmission {
  events: ApiEvent[];
}

function toItem(a: ApiItem): SubmissionItem {
  return {
    id: a.id,
    kind: a.kind,
    position: a.position,
    document: a.document,
    formSubmission: a.form_submission,
    label: a.label,
    formNumber: a.form_number,
  };
}

function toEvent(a: ApiEvent): SubmissionEvent {
  return {
    id: a.id,
    eventType: a.event_type,
    eventTypeDisplay: a.event_type_display,
    isSystem: a.is_system,
    detail: a.detail,
    createdAt: a.created_at,
  };
}

function toSubmission(a: ApiSubmission): IRSSubmission {
  return {
    id: a.id,
    client: a.client,
    case: a.case,
    channel: a.channel,
    channelDisplay: a.channel_display,
    status: a.status,
    statusDisplay: a.status_display,
    practitioner: a.practitioner,
    practitionerName: a.practitioner_name,
    practitionerCredentialsReady: a.practitioner_credentials_ready,
    irsConfirmationNumber: a.irs_confirmation_number,
    irsAckStatus: a.irs_ack_status,
    irsAckCode: a.irs_ack_code,
    errorMessage: a.error_message,
    receiptDocument: a.receipt_document,
    submittedAt: a.submitted_at,
    transmittedAt: a.transmitted_at,
    ackedAt: a.acked_at,
    createdAt: a.created_at,
    updatedAt: a.updated_at,
    items: a.items.map(toItem),
  };
}

function toSubmissionDetail(a: ApiSubmissionDetail): IRSSubmissionDetail {
  return { ...toSubmission(a), events: (a.events ?? []).map(toEvent) };
}

const base = (clientId: string) => `/clients/${clientId}/submissions`;

/** GET a client's IRS submissions. */
export async function listClientSubmissions(
  clientId: string,
): Promise<IRSSubmission[]> {
  const body = await apiFetch<Envelope<ApiSubmission[]>>(
    `${base(clientId)}/?page_size=100`,
  );
  return body.data.map(toSubmission);
}

/** GET one submission with its lifecycle events. */
export async function getSubmission(
  clientId: string,
  id: string,
): Promise<IRSSubmissionDetail> {
  const body = await apiFetch<Envelope<ApiSubmissionDetail>>(
    `${base(clientId)}/${id}/`,
  );
  return toSubmissionDetail(body.data);
}

/** POST create a draft submission from selected forms/documents. */
export async function createSubmission(
  clientId: string,
  payload: NewSubmissionPayload,
): Promise<IRSSubmissionDetail> {
  const body = await apiFetch<Envelope<ApiSubmissionDetail>>(`${base(clientId)}/`, {
    method: "POST",
    json: payload,
  });
  return toSubmissionDetail(body.data);
}

/** POST validate + queue a submission for the connector. */
export async function transmitSubmission(
  clientId: string,
  id: string,
  accessCode?: string,
): Promise<IRSSubmissionDetail> {
  const body = await apiFetch<Envelope<ApiSubmissionDetail>>(
    `${base(clientId)}/${id}/transmit/`,
    { method: "POST", json: accessCode ? { access_code: accessCode } : {} },
  );
  return toSubmissionDetail(body.data);
}

/** POST cancel a queued submission. */
export async function cancelSubmission(
  clientId: string,
  id: string,
): Promise<IRSSubmissionDetail> {
  const body = await apiFetch<Envelope<ApiSubmissionDetail>>(
    `${base(clientId)}/${id}/cancel/`,
    { method: "POST", json: {} },
  );
  return toSubmissionDetail(body.data);
}

/** DELETE a draft submission. */
export async function deleteSubmission(clientId: string, id: string): Promise<void> {
  await apiFetch<Envelope<null>>(`${base(clientId)}/${id}/`, { method: "DELETE" });
}

/** GET firm-level e-filing status + queue counts. */
export async function getEFilingStatus(): Promise<EFilingStatus> {
  const body = await apiFetch<
    Envelope<{
      efiling_enabled: boolean;
      efiling_down: boolean;
      efiling_message: string;
      connector_configured: boolean;
      queued_count: number;
      transmitted_count: number;
      pending_ack_count: number;
    }>
  >(`/efiling/status/`);
  return {
    efilingEnabled: body.data.efiling_enabled,
    efilingDown: body.data.efiling_down,
    efilingMessage: body.data.efiling_message,
    connectorConfigured: body.data.connector_configured,
    queuedCount: body.data.queued_count,
    transmittedCount: body.data.transmitted_count,
    pendingAckCount: body.data.pending_ack_count,
  };
}
